import type { ReactNode } from 'react'

import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { SxProps, Theme } from '@mui/material/styles'

import LogoTile from './LogoTile'
import { getLogoElevation } from './logoShadow'

interface LogoClusterCardProps {
  /** Brand/merchant logo visuals, rendered as a row of tiles. */
  logos: ReactNode[]
  /** Tile size in px. */
  logoSize?: number
  /** Card surface color/background token or CSS background string. */
  background?: string
  /**
   * Stagger the tiles diagonally (Figma cycles each tile's vertical alignment
   * top -> center -> bottom). When false, all tiles are vertically centered.
   * Default true.
   */
  stagger?: boolean
  /**
   * Overlay a 1-based rank badge on the top-left of each tile (Figma: Monthly
   * Top Merchant Spending V3). Default false.
   */
  ranked?: boolean
  /**
   * Accessible label for each rank badge, given the 1-based rank. Lets the
   * consumer pass localized copy (e.g. from the copy store) and control word
   * order. Defaults to `Rank {n}` — callers in a localized context must supply a
   * copy-store-derived function, or they will ship the English default.
   */
  rankLabel?: (rank: number) => string
  /**
   * Optional element rendered below the logo row (e.g. a `<StatusPill>`).
   * Figma: a status stacked beneath the logos on the slot.
   */
  footer?: ReactNode
  sx?: SxProps
}

// Default accessible label for a rank badge. Hoisted to a stable reference so
// it isn't re-created each render (consumers override via the `rankLabel` prop,
// e.g. with localized copy).
const defaultRankLabel = (rank: number) => `Rank ${rank}`

// Non-ranked (MSA V2): tiles cascade via counter-axis alignment in a 112 zone
// (tops 0/20/40).
const STAGGER_ALIGN = ['flex-start', 'center', 'flex-end'] as const
const STAGGER_STEP = 20
// Ranked (Top Merchant V3): exact Figma logo top-offsets within the 122 `Logos`
// content zone (logo2 bottom leaves ~7px). Not a uniform step, so use the
// measured values rather than alignSelf.
const RANK_TILE_OFFSETS = [10, 25, 43] as const
const RANK_ROW_HEIGHT = 122
// Figma: the rank badge overhangs the logo's top-left by 10px left / 8px up.
// The row reserves the left overhang once (so the first badge stays inside the
// card edge); the badge itself is offset by it via the MUI transform below.
const RANK_BADGE_OVERHANG = { x: 10, y: 8 } as const

// Figma rank badge on MUI `Badge`: a 24px pure-black disc, 1px white border,
// white `body2` number, soft elevation shadow. MUI centers the badge on the
// logo's corner (`translate(-50%,-50%)`); override the transform so its top-left
// lands exactly 10px left / 8px above the logo (Figma's overhang).
const rankBadgeSx: SxProps<Theme> = {
  '& .MuiBadge-badge': {
    // Intentional: `common.black`/`common.white` are mode-INDEPENDENT in
    // createMXTheme (unlike `text.primary`/`primary.contrastText`, which flip
    // per theme). A white-on-black disc plus the white border below keeps the
    // badge legible in BOTH light and dark mode — this is the dark-mode FIX, not
    // a bug. Do not "theme-aware"-ify these colors; it would break the Figma
    // look in light mode for no gain (the number sits on the always-black disc,
    // never on the surface).
    bgcolor: 'common.black',
    border: '1px solid',
    borderColor: 'common.white',
    borderRadius: '999px',
    boxShadow: (theme: Theme) => getLogoElevation(theme),
    color: 'common.white',
    // Figma number is `typography/body2` (13px / regular), not MUI's badge
    // default (12px / medium).
    fontSize: (theme: Theme) => theme.typography.body2.fontSize,
    fontWeight: (theme: Theme) => theme.typography.fontWeightRegular,
    height: 24,
    minWidth: 24,
    padding: 0,
    transform: `translate(-${RANK_BADGE_OVERHANG.x}px, -${RANK_BADGE_OVERHANG.y}px)`,
  },
}

/**
 * A row of rounded brand/logo tiles on a card surface, diagonally staggered
 * (top -> center -> bottom). Figma: Monthly Subscription Aggregate V2.
 */
const LogoClusterCard = ({
  background,
  footer,
  logoSize = 72,
  logos,
  ranked = false,
  rankLabel = defaultRankLabel,
  stagger = true,
  sx,
}: LogoClusterCardProps) => {
  // Non-ranked cascades via alignSelf in a `logoSize + span` zone. Ranked uses
  // the exact Figma offsets in the 122 zone (its cascade isn't a uniform step).
  const staggerSpan = stagger ? (STAGGER_ALIGN.length - 1) * STAGGER_STEP : 0
  const rowHeight = ranked ? RANK_ROW_HEIGHT : logoSize + staggerSpan
  // Figma `Logos` frame side padding: 32 for MSA V2, 16 for the ranked cluster.
  const rowPaddingX = ranked ? 16 : 32

  return (
    <Stack sx={{ pt: 8, px: 16, width: '100%', ...sx }}>
      <Stack
        sx={{
          alignItems: 'center',
          background: background ?? undefined,
          bgcolor: background ? undefined : 'background.paper',
          borderRadius: '8px',
          gap: 12,
          // Card (Figma `Wrapper`) has no horizontal padding of its own — the
          // logo row carries its side padding (`rowPaddingX`); only vertical.
          py: 16,
          width: '100%',
        }}
      >
        <Stack
          sx={{
            alignItems: 'flex-start',
            flexDirection: 'row',
            // Both variants use Figma's SPACE_BETWEEN (fills the row). Ranked
            // items reserve the badge overhang (below), so this yields Figma's
            // 26/18 side insets and ~14px visible gaps; MSA yields ~3px gaps.
            justifyContent: 'space-between',
            height: rowHeight,
            px: rowPaddingX,
            width: '100%',
          }}
        >
          {logos.map((logo, index) => {
            const tile = (
              <LogoTile borderColor="divider" shadow="elevation" size={logoSize}>
                {logo}
              </LogoTile>
            )
            // Ranked: offset each tile to its exact Figma top and size the item
            // to Figma's badge-inclusive box (72 logo + 10 left overhang + 2
            // right = 84), with the logo pushed right by the overhang. Then
            // SPACE_BETWEEN reproduces the 26/18 side insets and 14px gaps.
            // Non-ranked: cascade via alignSelf in the stagger zone.
            const positionSx = ranked
              ? {
                  boxSizing: 'border-box' as const,
                  mt: RANK_TILE_OFFSETS[index % RANK_TILE_OFFSETS.length],
                  pl: RANK_BADGE_OVERHANG.x,
                  width: logoSize + RANK_BADGE_OVERHANG.x + 2,
                }
              : { alignSelf: stagger ? STAGGER_ALIGN[index % STAGGER_ALIGN.length] : 'center' }

            return (
              // Index key is fine: the caller-supplied list is static.
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                sx={positionSx}
              >
                {ranked ? (
                  <Badge
                    anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                    aria-label={rankLabel(index + 1)}
                    // `role="img"` gives the root span a role that supports an
                    // accessible name, so aria-label is surfaced to AT (a bare
                    // generic span's aria-label is not announced).
                    role="img"
                    // Hide the visible number from AT so the badge is announced
                    // once via aria-label ("Rank 1"), not twice ("Rank 1 … 1").
                    badgeContent={<span aria-hidden={true}>{index + 1}</span>}
                    overlap="rectangular"
                    sx={rankBadgeSx}
                  >
                    {tile}
                  </Badge>
                ) : (
                  tile
                )}
              </Box>
            )
          })}
        </Stack>
        {footer && <Box sx={{ px: 16, width: '100%' }}>{footer}</Box>}
      </Stack>
    </Stack>
  )
}

export default LogoClusterCard
